import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff } from 'lucide-react';

interface VoiceInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'autoCorrect'> {
  onTranscript?: (text: string) => void;
  enableAutoCorrect?: boolean;
  dictionary?: string[];
}

const defaultDictionary = [
  'Sacrum', 'ischium', 'trochanter', 'heal', 'heel', 'serous', 'sanguinous',
  'purulent', 'exudate', 'allegation', 'safeguarding', 'DSL', 'CQC', 'MAR',
  'Waterlow', 'BICSc', 'COSHH', 'PPE', 'IPC', 'MDT', 'GP', 'NMC',
  'Norovirus', 'influenza', 'COVID-19', 'antimicrobial', 'virucidal'
];

export const VoiceInput: React.FC<VoiceInputProps> = ({
  onTranscript,
  enableAutoCorrect = true,
  dictionary = defaultDictionary,
  ...props
}) => {
  const [isListening, setIsListening] = useState(false);
  const [value, setValue] = useState(props.value || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    if (props.value !== undefined && props.value !== value) {
      setValue(props.value);
    }
  }, [props.value]);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-GB';

    recognition.onresult = (event: any) => {
      let final = '';
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          final += event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      const combined = final || interim;
      if (combined) {
        let corrected = combined;
        if (enableAutoCorrect) {
          dictionary.forEach(term => {
            const lowerTerm = term.toLowerCase();
            const regex = new RegExp(`\\b${lowerTerm}\\b`, 'gi');
            corrected = corrected.replace(regex, term);
          });
          corrected = corrected.replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
        }
        setValue(corrected);
        if (props.onChange) {
          const event = new Event('input', { bubbles: true }) as any;
          event.target = inputRef.current;
          event.currentTarget = inputRef.current;
          props.onChange(event as React.ChangeEvent<HTMLInputElement>);
        }
        if (onTranscript) onTranscript(corrected);
        if (final) setIsListening(false);
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
  }, [enableAutoCorrect, dictionary, onTranscript]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setValue('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (props.onChange) props.onChange(e);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        {...props}
        value={value}
        onChange={handleChange}
        className={`w-full border rounded px-3 py-2 text-sm pr-10 ${props.className || ''}`}
        spellCheck={enableAutoCorrect}
      />
      <button
        type="button"
        onClick={toggleListening}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full transition ${
          isListening ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
        }`}
        title={isListening ? 'Stop dictating' : 'Start dictating'}
      >
        {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </button>
      {isListening && (
        <div className="absolute -bottom-6 left-0 text-xs text-red-500 font-medium">
          🎤 Listening...
        </div>
      )}
    </div>
  );
};